import { NumberRangeFilterDef } from '@tanstack/react-table';
import { Trans, useTranslation } from '@sib/i18n';
import { isBinaryRangeOperator, RangeOperator } from '@sib/types';
import {
  BaseSelect,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  cn,
} from '@sib/ui';
import { X } from '@sib/ui/icons';
import { RangeInputField } from './RangeInputField';
import {
  RANGE_OPERATOR_ICONS,
  STANDARD_RANGE_OPERATORS,
  useNumberRangeFilter,
} from './NumberRangeFilter.utils';

interface NumberRangeFilterProps {
  value: string[];
  onChange?: (operator: RangeOperator, from: string, to: string) => void;
  onClear?: () => void;
  placeholder?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  defaultFrom?: number;
  defaultTo?: number;
  unit?: string;
  operators?: readonly RangeOperator[];
  filterProps?: NumberRangeFilterDef;
  size?: number;
}

export const NumberRangeFilter = ({
  value,
  onChange,
  onClear,
  placeholder,
  disabled = false,
  min = 0,
  max = 100,
  defaultFrom,
  defaultTo,
  unit,
  operators,
  filterProps,
  size = 280,
}: NumberRangeFilterProps) => {
  const { t } = useTranslation();

  const allowedOperators =
    operators ?? filterProps?.operators ?? STANDARD_RANGE_OPERATORS;

  const {
    operator,
    from,
    to,
    hasAppliedRange,
    isDefaultRange,
    buttonLabel,
    open,
    setOpen,
    handleOperatorChange,
    handleFromInputChange,
    handleToInputChange,
    handleClear,
    handleConfirm,
  } = useNumberRangeFilter(value, min, max, {
    defaultFrom,
    defaultTo,
    unit,
    placeholder,
    operators: allowedOperators,
    onChange,
    onClear,
  });

  const SelectedOperatorIcon = RANGE_OPERATOR_ICONS[operator];

  const { TriggerProps, ContentProps } = filterProps?.PopoverProps ?? {};

  const OPERATOR_GROUPS: RangeOperator[][] = [
    ['between', 'nbetween'],
    ['eq'],
    ['lt', 'lte'],
    ['gt', 'gte'],
  ];

  const visibleGroups = OPERATOR_GROUPS.map(group =>
    group.filter(op => allowedOperators.includes(op))
  ).filter(group => group.length > 0);

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="custom"
          variant="outline"
          aria-expanded={open}
          className={cn(
            'hover:text-primary relative h-[30px] w-full justify-between overflow-hidden border-0 px-2 py-1 hover:bg-white',
            TriggerProps?.className,
            disabled && 'cursor-not-allowed opacity-60'
          )}
          disabled={disabled}
          onClick={e => e.stopPropagation()}
          {...TriggerProps}
        >
          <span className="truncate">{buttonLabel}</span>
          {hasAppliedRange && (
            <span
              className="shrink-0 cursor-pointer"
              onClick={e => {
                e.stopPropagation();
                handleClear();
              }}
            >
              <X className="size-4" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-full p-0', ContentProps?.className)}
        style={{ width: Math.max(size, 280) }}
        align="center"
        onClick={e => e.stopPropagation()}
        {...ContentProps}
      >
        <div className="flex flex-col gap-3 px-3 pt-1 pb-3">
          <div className="flex flex-col gap-2">
            <span className="text-primary px-2 py-1.5 text-sm font-medium">
              <Trans
                i18nKey="components.numberRangeFilter.selectRangeType"
                values={{ unit: unit ? `(${unit})` : '' }}
                components={{
                  muted: <span className="text-muted-foreground font-normal" />,
                }}
              />
            </span>
            <BaseSelect
              value={operator}
              onValueChange={value =>
                handleOperatorChange(value as RangeOperator)
              }
            >
              <SelectTrigger>
                <SelectValue>
                  <span className="flex items-center gap-2">
                    <SelectedOperatorIcon className="text-muted-foreground size-4 shrink-0" />
                    {t(`components.numberRangeFilter.operators.${operator}`)}
                  </span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {visibleGroups.map((group, groupIndex) => (
                  <span key={groupIndex}>
                    {groupIndex > 0 && <SelectSeparator />}
                    {group.map(operator => {
                      const Icon = RANGE_OPERATOR_ICONS[operator];

                      return (
                        <SelectItem key={operator} value={operator}>
                          <span className="flex items-center gap-2">
                            <Icon className="text-primary size-4 shrink-0" />
                            {t(
                              `components.numberRangeFilter.operators.${operator}`
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </span>
                ))}
              </SelectContent>
            </BaseSelect>
          </div>
          <div className="flex w-full justify-between gap-2">
            {isBinaryRangeOperator(operator) ? (
              <>
                <RangeInputField
                  label={t('from')}
                  value={from}
                  min={min}
                  max={max}
                  onChange={handleFromInputChange}
                />
                <RangeInputField
                  label={t('to')}
                  value={to}
                  min={min}
                  max={max}
                  onChange={handleToInputChange}
                />
              </>
            ) : (
              <RangeInputField
                label={t(`components.numberRangeFilter.operators.${operator}`)}
                value={from}
                min={min}
                max={max}
                onChange={handleFromInputChange}
                fullWidth
              />
            )}
          </div>
          <div className="flex items-center justify-center gap-4 pt-3">
            <Button
              disabled={isDefaultRange}
              variant="outline"
              onClick={handleClear}
            >
              {t('clear')}
            </Button>
            <Button disabled={isDefaultRange} onClick={handleConfirm}>
              {t('confirm')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
